import { Box, ToggleButton } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useEffect, useState } from 'react';

export interface ExpandingToggleButtonProps {
  value: string;
  handleClick: (value: string) => void;
  mainButtonValue: string;
  icon: ReactNode;
  name: string;
  nestedButtonProps: nestedToggleButtonProps[];
}

export interface nestedToggleButtonProps {
  value: string;
  label?: string;
  icon: ReactNode;
}

/**
 *
 * @param value controlled input value of the entire group
 * @param handleClick function invoked when any button is clicked
 * @param mainButtonValue value of the main button
 * @param icon icon for the main button
 * @param name label for the main button
 * @param nestedButtonProps array of props for each inner button
 * @returns
 */
export const ExpandingToggleButtonGroup: React.FC<
  ExpandingToggleButtonProps
> = ({
  value,
  handleClick,
  icon,
  name,
  nestedButtonProps,
  mainButtonValue,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleNestedButtonClick = (value: string) => {
    if (value === mainButtonValue) {
      setExpanded(!expanded);
    }
    handleClick(value);
  };

  // expand if an inner button is selected
  useEffect(() => {
    if (
      value !== mainButtonValue &&
      !nestedButtonProps.some((props) => props.value === value)
    ) {
      setExpanded(false);
    }
  }, [value]);

  const backgroundStyle = {
    display: 'flex',
    padding: '0.25rem',
    width: '3rem',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: '0.25rem',
    background: '#2E2E2E',
  };

  const buttonStyle = {
    display: 'flex',
    width: '2.5rem',
    height: '2rem',
    padding: '0',
    flexDirection: 'column',
    alignItems: 'center',
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: '#454545',
      borderRadius: '0.1875rem',
      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.25)',
    },
    border: '0',
  };

  return (
    <Box sx={backgroundStyle} component={motion.div} layout='position'>
      <ToggleButton
        fullWidth
        sx={buttonStyle}
        key={name}
        value={mainButtonValue}
        selected={value === mainButtonValue}
        aria-label={name}
        onClick={(_event, value) => handleNestedButtonClick(value)}
        title={name}
      >
        {icon}
      </ToggleButton>
      <AnimatePresence mode='wait' initial={false}>
        {expanded &&
          nestedButtonProps.map((props) => (
            <motion.div
              title={props.label}
              aria-label={props.label}
              key={props.label}
              initial={{
                height: 0,
                opacity: 0,
              }}
              animate={{
                height: 'auto',
                opacity: 1,
                transition: {
                  height: {
                    duration: 0.4,
                  },
                  opacity: {
                    duration: 0.2,
                    delay: 0.2,
                  },
                },
              }}
              exit={{
                height: 0,
                opacity: 0,
                transition: {
                  height: {
                    delay: 0.1,
                    duration: 0.3,
                  },
                  opacity: {
                    duration: 0.2,
                  },
                },
              }}
            >
              <ToggleButton
                sx={{
                  ...buttonStyle,
                  // using margin instead of gap to eliminate animation jitter
                  margin: '0.25rem 0 0 0',
                }}
                fullWidth
                key={props.value}
                value={props.value}
                selected={value === props.value}
                onClick={(_event, value) => handleNestedButtonClick(value)}
              >
                {props.icon}
              </ToggleButton>
            </motion.div>
          ))}
      </AnimatePresence>
    </Box>
  );
};
